import { useRef, useEffect } from 'react'
import { ChevronDown, ChevronRight, Calendar, Users } from 'lucide-react'
import type { PeriodGroup } from '@/services/api/timetable'

interface PeriodGroupPanelProps {
  group: PeriodGroup
  isExpanded: boolean
  selectedTeachers: Set<string>
  onToggleExpand: () => void
  onSelectAll: (selected: boolean) => void
  onToggleTeacher: (teacherId: string) => void
}

export function PeriodGroupPanel({
  group,
  isExpanded,
  selectedTeachers,
  onToggleExpand,
  onSelectAll,
  onToggleTeacher,
}: PeriodGroupPanelProps) {
  const checkboxRef = useRef<HTMLInputElement>(null)
  const selectedCount = group.teachers.filter(t => selectedTeachers.has(t.teacher_id)).length
  const allSelected = selectedCount === group.teachers.length && group.teachers.length > 0
  const someSelected = selectedCount > 0 && selectedCount < group.teachers.length

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.checked = allSelected
      checkboxRef.current.indeterminate = someSelected
    }
  }, [allSelected, someSelected])

  // Get status badge
  const getStatusBadge = () => {
    if (group.is_current) {
      return (
        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-medium">
          Aktuell
        </span>
      )
    }
    
    if (group.valid_to) {
      const validTo = new Date(group.valid_to)
      const today = new Date()
      if (validTo < today) {
        return (
          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">
            Abgelaufen
          </span>
        )
      }
    }
    
    if (group.valid_from) {
      const validFrom = new Date(group.valid_from)
      const today = new Date()
      if (validFrom > today) {
        return (
          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full">
            Zukünftig
          </span>
        )
      }
    }
    
    return null
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
          isExpanded ? 'bg-gray-50 border-b' : 'bg-white'
        }`}
        onClick={onToggleExpand}
      >
        {/* Expand icon */}
        <button
          type="button"
          className="p-1 hover:bg-gray-200 rounded transition-colors"
          onClick={(e) => {
            e.stopPropagation()
            onToggleExpand()
          }}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
        </button>

        {/* Group checkbox */}
        <div className="relative">
          <input
            ref={checkboxRef}
            type="checkbox"
            checked={allSelected}
            onChange={(e) => {
              e.stopPropagation()
              onSelectAll(e.target.checked)
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
        </div>

        {/* Period info */}
        <div className="flex-1 flex items-center gap-3">
          <Calendar className="h-4 w-4 text-gray-400" />
          <div>
            <span className={`font-medium text-sm ${group.is_current ? 'text-green-800' : 'text-gray-700'}`}>
              {group.label}
            </span>
            <div className="flex items-center gap-2 mt-0.5">
              <Users className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-500">
                {group.teacher_count} Lehrer, {group.entry_count} Einträge
              </span>
            </div>
          </div>
        </div>

        {/* Status badge */}
        <div className="flex-shrink-0">
          {getStatusBadge()}
        </div>
      </div>

      {/* Teacher list */}
      {isExpanded && (
        <div className="bg-white p-3 space-y-1">
          <div className="flex items-center justify-between pb-2 border-b text-xs text-gray-500">
            <span>Lehrer</span>
            <span>Einträge</span>
          </div>
          
          {group.teachers.map((teacher) => (
            <div
              key={teacher.teacher_id}
              className={`flex items-center justify-between p-2 rounded hover:bg-gray-50 transition-colors ${
                selectedTeachers.has(teacher.teacher_id) ? 'bg-blue-50' : ''
              }`}
              onClick={() => onToggleTeacher(teacher.teacher_id)}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedTeachers.has(teacher.teacher_id)}
                  onChange={() => onToggleTeacher(teacher.teacher_id)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {teacher.teacher_name}
                  </span>
                  {teacher.teacher_code && (
                    <span className="text-xs text-gray-500 ml-2">
                      ({teacher.teacher_code})
                    </span>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-500">
                {teacher.entry_count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
